/**
 * src/lib/voice/ttsProvider.js — Client-safe TTS helpers (Phase 90-06)
 *
 * CLIENT-SAFE ONLY: naming constants, localStorage preference, pure utilities.
 * Provider implementations (makeOpenAiProvider, geminiProvider, callGeminiTts,
 * getTtsProvider) and synthWithFallback live in lib/voice/ttsProvider.server.js
 * (TICKET-SEC-2026-07-06 M1 — keep env-key reads and providerCall out of Vite bundle).
 *
 * Providers:
 *   - 'openai-tts1'         OpenAI TTS-1 (default). Fast streaming MP3.
 *   - 'openai-tts1-hd'      OpenAI TTS-1 HD. Higher quality, slower.
 *   - 'gemini-3.1-flash-tts' Gemini 3.1 Flash TTS. Expressive, NO streaming.
 *
 * Resolution order (client preference, used only as a hint to the server):
 *   localStorage.acn_tts_provider → null
 */

// llm-registry-ok: TTS_PROVIDER_NAMES contains routing keys (provider identifiers), not API model IDs
export const TTS_PROVIDER_NAMES = ['openai-tts1', 'openai-tts1-hd', 'gemini-3.1-flash-tts']; // llm-registry-ok: provider routing keys

const LS_KEY = 'acn_tts_provider';

// ── Resolution ──────────────────────────────────────────────────────────

export function resolveDefaultProvider({ envProvider, clientProvider } = {}) {
  if (envProvider && TTS_PROVIDER_NAMES.includes(envProvider)) return envProvider;
  if (clientProvider && TTS_PROVIDER_NAMES.includes(clientProvider)) return clientProvider;
  return 'openai-tts1';
}

export function getStoredTtsProvider() {
  if (typeof localStorage === 'undefined') return null;
  try {
    const v = localStorage.getItem(LS_KEY);
    return TTS_PROVIDER_NAMES.includes(v) ? v : null;
  } catch {
    return null;
  }
}

export function setStoredTtsProvider(name) {
  if (!TTS_PROVIDER_NAMES.includes(name)) {
    throw new Error(`Invalid TTS provider: ${name}. Supported: ${TTS_PROVIDER_NAMES.join(', ')}`);
  }
  if (typeof localStorage === 'undefined') return;
  try { localStorage.setItem(LS_KEY, name); } catch { /* noop */ }
}

// ── Format negotiation + cost (TICKET-WISPR-TTS-STREAM) ─────────────────

/**
 * Negotiate the audio format a provider will actually emit. 'pcm' only when
 * requested AND supported; everything else lands on 'mp3' (every provider's
 * lowest common denominator here).
 */
export function resolveTtsFormat(provider, requested) {
  if (requested === 'pcm' && Array.isArray(provider?.formats) && provider.formats.includes('pcm')) {
    return 'pcm';
  }
  return 'mp3';
}

/** Content-Type the API route should send for a negotiated format. */
export function ttsContentType(format) {
  // OpenAI PCM is fixed at 24kHz 16-bit LE mono; the rate param lets the
  // client's pcmPlayer pick the right sample rate without hardcoding.
  return format === 'pcm' ? 'audio/pcm;rate=24000' : 'audio/mpeg';
}

// $ per input character, by provider routing key (PAID-API GUARDRAIL pillar 1
// — cost observability). OpenAI TTS pricing is per 1M input characters.
// Gemini TTS is token-priced; cost left null (chars still logged, reconciled
// in the cost view later) rather than guessed.
// TICKET-1208: kept as literals — src/ ESLint rejects JSON import attributes and
// Node (ttsProvider.server.js imports this file) needs them. Drift vs
// config/llm-models.json _prices is guarded by tests/lib/voice/voice-registry-ticket-1208.test.js.
// llm-registry-ok: keys are provider routing identifiers, not API model IDs
const TTS_COST_PER_CHAR = {
  'openai-tts1': 15 / 1e6, // llm-registry-ok: provider routing key, not an API model ID
  'openai-tts1-hd': 30 / 1e6, // llm-registry-ok: provider routing key, not an API model ID
  'gemini-3.1-flash-tts': null, // llm-registry-ok: provider routing key, not an API model ID
};

/** Caller-computed cost for logProviderUsage. Returns null when unknown. */
export function estimateTtsCostUsd(providerName, chars) {
  const perChar = TTS_COST_PER_CHAR[providerName];
  if (perChar == null || !Number.isFinite(chars)) return null;
  return chars * perChar;
}

// ── Fallback / reliability (TICKET-073 Slice 2) ──────────────────────────

// Only auth/config errors are non-retryable — re-trying them on another
// provider won't help (the key/config is wrong) and the SCOPE says surface
// them immediately. Everything else (rate_limit, timeout, 5xx, credits,
// network/unknown) is worth a second provider before we drop the voice.
const NON_RETRYABLE_TTS_KINDS = new Set(['auth', 'context_too_large']);

/** True when a TTS provider error is worth retrying on a DIFFERENT provider. */
export function isRetryableTtsError(err) {
  if (!err) return false;
  const kind = err.providerKind;
  if (!kind) return true; // a raw throw (e.g. network) — worth one fallback
  return !NON_RETRYABLE_TTS_KINDS.has(kind);
}

/**
 * Ordered providers to try AFTER the primary: same family first (same voice,
 * covers a transient model-endpoint hiccup), then cross-provider (covers an
 * account-wide outage). Excludes the primary; unknown primary → no fallbacks.
 */
export function getTtsFallbackChain(primaryName) {
  // llm-registry-ok: fallback chain keyed by provider routing identifiers, not API model IDs
  const chains = {
    'openai-tts1':          ['openai-tts1-hd', 'gemini-3.1-flash-tts'],  // llm-registry-ok: fallback chain routing keys
    'openai-tts1-hd':       ['openai-tts1', 'gemini-3.1-flash-tts'],     // llm-registry-ok: fallback chain routing keys
    'gemini-3.1-flash-tts': ['openai-tts1', 'openai-tts1-hd'],           // llm-registry-ok: fallback chain routing keys
  };
  return chains[primaryName] || [];
}
